import Link from "next/link";
import Image from "next/image";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import DefaultLogoIcon from "@/components/_icons/defaultLogoIcon";
import useMediaQuery from "@/utils/useMediaQuery";
import { useNativeActions } from "@/utils/useNativeActions";
import clsx from "clsx";
import type { Merchant } from "@/dto/merchant";
import {
  getHardNavigationHref,
  shouldHardNavigate,
} from "@/utils/hardNavigation";

interface MerchantLogoProps {
  merchantLogo?: string;
  showLink: boolean;
  linkHref: string | object;
  onImageLoad?: () => void;
  className?: string;
  merchant?: Pick<
    Merchant,
    | "isRetail"
    | "isService"
    | "isRestaurant"
    | "isHybrid"
    | "isEssentialMerchant"
    | "hasOnlyRetailItems"
  >;
}

export const MerchantLogo = ({
  merchantLogo,
  showLink,
  linkHref,
  onImageLoad,
  className,
  merchant,
}: MerchantLogoProps) => {
  const isMobileView = useMediaQuery("(max-width: 640px)");
  const { isCmaApp } = useNativeActions();
  let dimensions;

  if (isCmaApp) {
    dimensions = 32;
  } else if (isMobileView) {
    dimensions = 24;
  } else {
    dimensions = 100;
  }

  const logoClass = clsx(
    `sm:block h-6 w-6 sm:h-16 sm:w-16`,
    {
      "h-8 w-8": isCmaApp,
    },
    className
  );

  const image = merchantLogo ? (
    <Image
      data-testid={applyDataTestId(pom.header.merchantLogoDataTestId)}
      className={logoClass}
      src={merchantLogo}
      alt={pom.header.merchantLogo}
      width={dimensions}
      height={dimensions}
      onLoad={onImageLoad}
    />
  ) : (
    <DefaultLogoIcon
      data-testid={applyDataTestId(pom.header.merchantLogoDataTestId)}
      className={logoClass}
      aria-label={pom.header.merchantLogo}
    />
  );

  if (showLink) {
    const shouldUseHardNavigation =
      merchant && shouldHardNavigate(linkHref, merchant);

    if (shouldUseHardNavigation) {
      return <a href={getHardNavigationHref(linkHref)}>{image}</a>;
    }

    return (
      <Link href={linkHref}>
        {image}
      </Link>
    );
  }

  return image;
};
